import React, { useState } from 'react';
import { Heart, ShoppingBag, X, Truck } from 'lucide-react';
import { formatNaira } from '../utils/currency';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
import CartConfirmationModal from '../components/CartConfirmationModal';
import { useProductContext } from '../context/ProductContext';

interface Color {
  name: string;
  hex: string;
  image: string;
}

interface ProductModalProps {
  product: any;
  onClose: () => void;
}

function ProductModal({ product, onClose }: ProductModalProps) {
  const dispatch = useDispatch();
  const [selectedColor, setSelectedColor] = useState<Color | null>(product.colors?.[0] || null);
  const [selectedSize, setSelectedSize] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'description' | 'shipping'>('description');
  const [showConfirmation, setShowConfirmation] = useState(false);

  if (!product) return null;

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) return;

    dispatch(addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      image: selectedColor.image,
      selectedSize,
      selectedColor,
    }));

    setShowConfirmation(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors z-10"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
            {/* Product Image */}
            <div className="relative">
              <img
                src={selectedColor?.image || product.image}
                alt={`${product.name} in ${selectedColor?.name || 'default'}`}
                className="w-full h-[400px] object-cover rounded-xl"
              />
              <button className="absolute top-4 right-4 p-3 rounded-full bg-white/80 backdrop-blur-sm text-gray-600 hover:text-red-500 transition-colors">
                <Heart className="h-5 w-5" />
              </button>
            </div>

            {/* Product Details */}
            <div className="flex flex-col">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h2>
              <p className="text-gray-600 mb-4">{product.category}</p>
              <span className="text-2xl font-bold text-gray-900 mb-6">{formatNaira(product.price)}</span>

              {/* Color Selection */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">Select Color</h3>
                <div className="flex gap-3">
                  {product.colors?.map((color: Color) => (
                    <button
                      key={color.name}
                      onClick={() => setSelectedColor(color)}
                      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center ${
                        selectedColor?.name === color.name
                          ? 'border-pink-600'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      title={color.name}
                    >
                      <span
                        className={`w-8 h-8 rounded-full ${
                          color.name.toLowerCase() === 'white' ? 'border border-gray-200' : ''
                        }`}
                        style={{ backgroundColor: color.hex }}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {selectedColor?.name || 'Choose a color'}
                </p>
              </div>

              {/* Size Selection */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">Select Size</h3>
                <div className="grid grid-cols-3 gap-2">
                  {[37, 38, 39, 40, 41, 42].map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`py-3 rounded-lg border-2 ${
                        selectedSize === size
                          ? 'border-pink-600 bg-pink-50 text-pink-600'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      EU {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description and Shipping Tabs */}
              <div className="mb-6">
                <div className="flex border-b border-gray-200">
                  <button
                    className={`py-2 px-4 font-medium ${
                      activeTab === 'description'
                        ? 'text-pink-600 border-b-2 border-pink-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('description')}
                  >
                    Description
                  </button>
                  <button
                    className={`py-2 px-4 font-medium ${
                      activeTab === 'shipping'
                        ? 'text-pink-600 border-b-2 border-pink-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('shipping')}
                  >
                    Shipping
                  </button>
                </div>
                <div className="mt-4">
                  {activeTab === 'description' ? (
                    <p className="text-gray-600">{product.description}</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Truck className="h-5 w-5 text-pink-600 mt-1" />
                        <div>
                          <h4 className="font-medium text-gray-900">Shipping Information</h4>
                          <p className="text-gray-600">Shipping fee depends on the location</p>
                        </div>
                      </div>
                      <div className="bg-pink-50 p-4 rounded-lg">
                        <p className="text-sm text-pink-700">
                          Estimated delivery time will be provided at checkout based on your location
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className={`w-full bg-pink-600 text-white px-6 py-4 rounded-full flex items-center justify-center gap-2 ${
                  !selectedSize || !selectedColor ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-700'
                } transition-colors mt-auto`}
                disabled={!selectedSize || !selectedColor}
              >
                <ShoppingBag className="h-5 w-5" />
                {!selectedColor 
                  ? 'Select a Color'
                  : !selectedSize 
                    ? 'Select a Size' 
                    : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add confirmation modal */}
      {showConfirmation && (
        <CartConfirmationModal
          onClose={() => {
            setShowConfirmation(false);
            onClose();
          }}
          onContinueShopping={() => {
            setShowConfirmation(false);
            onClose();
          }}
        />
      )}
    </>
  );
}

function Women() {
  const { products, loading, error } = useProductContext();
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  // Filter women's products
  const womensProducts = products.filter(product => product.category === 'Women');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading products: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Banner */}
      <div className="relative h-[400px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=300&fit=crop"
          alt="Women's Collection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Elegance in Every Step</h1>
            <p className="text-xl text-white">Discover your perfect pair from our curated collection</p>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {womensProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden group cursor-pointer"
              onClick={() => setSelectedProduct(product)}
            >
              <div className="relative">
                <img
                  src={product.colors?.[0]?.image || product.image}
                  alt={product.name}
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <button
                  className="absolute top-4 right-4 p-3 rounded-full bg-white/80 backdrop-blur-sm text-gray-600 hover:text-red-500 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle wishlist logic here
                  }}
                >
                  <Heart className="h-5 w-5" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                  <h3 className="text-xl font-medium text-white">{product.name}</h3>
                  <p className="text-sm text-white/80">{product.category}</p>
                </div>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-2xl font-bold text-gray-900">{formatNaira(product.price)}</span>
                  {product.rating && (
                    <div className="flex items-center gap-1">
                      <span className="text-yellow-500">★</span>
                      <span className="text-gray-600">{product.rating}</span>
                    </div>
                  )}
                </div>
                {product.colors && (
                  <div className="flex gap-1 mt-2">
                    {product.colors.map((color: Color) => (
                      <span
                        key={color.name}
                        className={`w-4 h-4 rounded-full border ${
                          color.name.toLowerCase() === 'white' ? 'border-gray-200' : ''
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}

export default Women;